'use client';
import { NotificationService, useAppLoader, useIntl } from '@capsicum/common';
import { Snackbar } from '@capsicum/common-web';
import {
	type GeetestSuccessResponse,
	type MobileNumberValidationParams,
	type PhoneNumberFormattedMedium,
	type SmsLoginFormType,
	useMobileNumberValidation,
	useOnboardingAnalytics,
	useSendVerificationCode,
	useSmsLoginForm
} from '@capsicum/socios-core';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { useAtomValue } from 'jotai';
import { type FC, useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { attemptAtom } from './atoms';
import { CodeVerification } from './code-verification/CodeVerification';
import { Register } from './registration';
import UserDetails from './user-details/UserDetails';

export const Login: FC<{}> = () => {
	const loader = useAppLoader();
	const { $t } = useIntl();
	const checkMobileNumberAvailability = useMobileNumberValidation();
	const { getData } = useVisitorData({ extendedResult: true, ignoreCache: true }, { immediate: false });
	const { sendEmailCodeByMobileNumberToUser, sendSmsCodeToUser, sendSmsCodeToUserOnRegistration } =
		useSendVerificationCode();
	const { sendRegistrationPhoneEvent } = useOnboardingAnalytics();

	// State

	const [captchaResponse, setCaptchaResponse] = useState<GeetestSuccessResponse>();
	const [sendToRegistration, setSendToRegistration] = useState(false);
	const [formattedValues, setFormattedValues] = useState<PhoneNumberFormattedMedium>();
	const [validationParams, setValidationParams] = useState<MobileNumberValidationParams>();
	const [stage, setStage] = useState<'code-verification' | 'registration' | 'user-details'>('user-details');
	const attempt = useAtomValue(attemptAtom);
	const [isRequestNewCode, setIsRequestNewCode] = useState(false);
	const [verificationCode, setVerificationCode] = useState<string>();

	const smsloginForm = useSmsLoginForm(); // Holds only the login details without captcha

	const onSendBySms = useCallback(
		() =>
			validationParams && captchaResponse
				? (async () => {
						setCaptchaResponse(undefined);
						try {
							await loader(
								(sendToRegistration ? sendSmsCodeToUserOnRegistration : sendSmsCodeToUser)(
									{
										...validationParams,
										attempt: String(attempt),
										captchaInformation: {
											captchaId: captchaResponse.captcha_id,
											captchaOutput: captchaResponse.captcha_output,
											genTime: captchaResponse.gen_time,
											lotNumber: captchaResponse.lot_number,
											passToken: captchaResponse.pass_token
										}
									},
									() => getData()
								)
							);

							setStage('code-verification');
						} catch (err) {
							showErrorToast($t('auth.welcome.codeInput.retrySMS'));
						}
					})()
				: Promise.reject(new Error('validationParams or captchaResponse are undefined')),
		[
			validationParams,
			loader,
			captchaResponse,
			attempt,
			sendSmsCodeToUser,
			sendSmsCodeToUserOnRegistration,
			sendToRegistration,
			getData
		]
	);

	const onSendByEmail = useCallback(
		() =>
			validationParams
				? (async () => {
						try {
							await loader(sendEmailCodeByMobileNumberToUser(validationParams));
							setStage('code-verification');
						} catch (err) {
							return Promise.reject(err);
						}
					})()
				: Promise.reject(new Error('validationParams are undefined')),
		[validationParams, loader, sendEmailCodeByMobileNumberToUser]
	);

	const showErrorToast = useCallback(
		(description = $t('auth.geetestError')) =>
			NotificationService.addNotification({
				component: <Snackbar description={description} title={$t('global.tryAgainLater')} variant="error" />
			}),
		[$t]
	);

	const onReset = useCallback(() => {
		setStage('user-details');
		setIsRequestNewCode(true);
		setFormattedValues(undefined);
	}, [setStage, setFormattedValues]);

	const validateMobileNumber = useCallback(
		async (submittedData: SmsLoginFormType) => {
			const params = {
				countryCode: submittedData.country.iso2,
				mobileNumber: submittedData.mobileNumber
			};

			try {
				const { error, formattedValues: fV } = await checkMobileNumberAvailability(params);

				if (error) {
					const { field, ...rest } = error;

					smsloginForm.setError(field, rest);
				} else if (fV) {
					// Storing these in state so that they can be used later (get code by email)
					setFormattedValues(fV);
					setSendToRegistration(!!(fV.isAvailable && fV.isValid));
					setValidationParams(params);

					return fV.isValid ?? false;
				}
			} catch {
				showErrorToast();
			}

			return false;
		},
		[checkMobileNumberAvailability, showErrorToast, smsloginForm.setError]
	);

	const goToRegistration = useCallback((code: string) => {
		setStage('registration');
		setVerificationCode(code);
	}, []);

	// Effects

	useEffect(() => {
		const sub = smsloginForm.watch(() => {
			setIsRequestNewCode(false);
		});

		return () => sub.unsubscribe();
	}, [smsloginForm.watch]);

	useEffect(() => {
		// Initial SMS code send
		if (validationParams && captchaResponse) {
			onSendBySms();
		}
	}, [validationParams, captchaResponse, onSendBySms]);

	useEffect(() => {
		sendToRegistration && stage === 'code-verification' && sendRegistrationPhoneEvent();
	}, [sendToRegistration, stage, sendRegistrationPhoneEvent]);

	// JSX

	if (formattedValues && stage === 'code-verification')
		return (
			<CodeVerification
				formattedValues={formattedValues}
				goToRegistration={goToRegistration}
				reset={onReset}
				sendByEmail={onSendByEmail}
				sendToRegistration={sendToRegistration}
			/>
		);
	else if (stage === 'registration' && verificationCode && formattedValues?.mobileNumber) {
		return (
			<Register
				countryCode={formattedValues.countryCode}
				mobileNumber={formattedValues.mobileNumber}
				type="phone-number"
				validationCode={verificationCode}
			/>
		);
	}

	return (
		<FormProvider {...smsloginForm}>
			<UserDetails
				isRequestNewCode={isRequestNewCode}
				validateMobileNumber={validateMobileNumber}
				onCaptchaResponseChange={setCaptchaResponse}
			/>
		</FormProvider>
	);
};
